import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  timer,
  map,
  switchMap,
  takeWhile,
  last,
  catchError,
  finalize,
  firstValueFrom
} from 'rxjs';
import { GraphService } from 'src/app/services/api/graph.service';
import { OrderRecalcHistoryItem } from '../model/order.model';
import {
  CREATE_ORDER_JOB_POLL_INTERVAL_MS,
  CREATE_ORDER_JOB_TIMEOUT_MS,
  ConfirmPreliminaryOrderResult,
  PreliminaryOrdersBatchResult,
  CreateOrderInput,
  CreateOrderJobResult,
  CreateOrderJobStatus,
  isCreateOrderJobInProgress
} from '../model/create-order.model';
import {
  DeleteOrderInput,
  UpdateOrderReservationNumberInput,
  UpdateOrderReservationStatusInput,
  UpdateOrderResult,
  UpdateOrderScheduledPaymentDateInput
} from '../model/update-order.model';
import {
  CONFIRM_PRELIMINARY_ORDER_MUTATION,
  CREATE_ORDER_MUTATION,
  RECALCULATE_PRELIMINARY_ORDER_MUTATION,
  UPDATE_ORDER_FREEZE_DEADLINE_MUTATION,
  CREATE_ORDER_PDF_DOWNLOAD_URL_MUTATION,
  DELETE_ORDER_MUTATION,
  GET_CREATE_ORDER_JOB_QUERY,
  GET_ORDER_RECALC_HISTORY_QUERY,
  GET_ORDERS_AVAILABLE_PAGE_QUERY,
  RETRY_GENERATE_ORDER_PDF_MUTATION,
  UPDATE_ORDER_RESERVATION_NUMBER_MUTATION,
  UPDATE_ORDER_RESERVATION_STATUS_MUTATION,
  UPDATE_ORDER_SCHEDULED_PAYMENT_DATE_MUTATION
} from './order.mutations';
import {
  OrderPdfDownloadUrlResult,
  RetryGenerateOrderPdfResult
} from '../model/order-pdf.model';
import {
  GetOrdersAvailableInput,
  GetOrdersAvailableResult,
  OrderAvailableItem
} from '../model/orders-available.model';
import {
  createGraphqlClientError,
  logOrderDevDetail
} from '../lib/graphql-error.utils';

const ORDERS_AVAILABLE_PAGE_LIMIT = 100;
/** Safety cap: evita loop infinito si el backend devolviera lastKey eterno. */
const ORDERS_AVAILABLE_MAX_PAGES = 200;

export interface OrdersAvailablePageProgress {
  items: OrderAvailableItem[];
  /** Acumulado hasta esta página (inclusive). */
  loadedCount: number;
  hasMore: boolean;
  pageIndex: number;
}

export type OrdersAvailableProgressCallback = (
  progress: OrdersAvailablePageProgress
) => void;
@Injectable({ providedIn: 'root' })
export class OrderService {
  private readonly createOrderBusySubject = new BehaviorSubject<boolean>(false);
  readonly createOrderBusy$ = this.createOrderBusySubject.asObservable();

  constructor(private readonly graph: GraphService) {}

  get isCreateOrderBusy(): boolean {
    return this.createOrderBusySubject.value;
  }

  confirmPreliminaryOrder(
    orderId: string
  ): Observable<ConfirmPreliminaryOrderResult> {
    return this.graph
      .mutateGraphQLCore<{
        confirmPreliminaryOrder: ConfirmPreliminaryOrderResult;
      }>({
        mutation: CONFIRM_PRELIMINARY_ORDER_MUTATION,
        variables: { orderId }
      })
      .pipe(map((data: any) => data?.confirmPreliminaryOrder));
  }

  recalculatePreliminaryOrder(
    orderId: string
  ): Observable<PreliminaryOrdersBatchResult> {
    return this.graph
      .mutateGraphQLCore<{
        recalculatePreliminaryOrder: PreliminaryOrdersBatchResult;
      }>({
        mutation: RECALCULATE_PRELIMINARY_ORDER_MUTATION,
        variables: { orderId }
      })
      .pipe(
        map(
          (data: any) =>
            data?.recalculatePreliminaryOrder ?? { updatedCount: 0, orderIds: [] }
        )
      );
  }

  getOrderRecalcHistory(orderId: string): Observable<OrderRecalcHistoryItem[]> {
    return this.graph
      .queryGraphQLCore<{ getOrderRecalcHistory: OrderRecalcHistoryItem[] }>({
        query: GET_ORDER_RECALC_HISTORY_QUERY,
        variables: { orderId }
      })
      .pipe(map((data: any) => data?.getOrderRecalcHistory ?? []));
  }

  updateOrderFreezeDeadline(
    orderId: string,
    freezeDeadlineAt: string
  ): Observable<ConfirmPreliminaryOrderResult> {
    return this.graph
      .mutateGraphQLCore<{
        updateOrderFreezeDeadline: ConfirmPreliminaryOrderResult;
      }>({
        mutation: UPDATE_ORDER_FREEZE_DEADLINE_MUTATION,
        variables: { orderId, freezeDeadlineAt }
      })
      .pipe(map((data: any) => data?.updateOrderFreezeDeadline));
  }

  createOrder(input: CreateOrderInput): Observable<CreateOrderJobResult> {
    return this.graph
      .mutateGraphQLCore<{ createOrder: CreateOrderJobResult }>({
        mutation: CREATE_ORDER_MUTATION,
        variables: { input }
      })
      .pipe(map((data: any) => this.normalizeJob(data?.createOrder)));
  }

  getCreateOrderJob(jobId: string): Observable<CreateOrderJobResult> {
    return this.graph
      .queryGraphQLCore<{ getCreateOrderJob: CreateOrderJobResult } | {
        data?: { getCreateOrderJob: CreateOrderJobResult };
        errors?: Array<{ message: string; extensions?: { code?: string } }>;
      }>({
        query: GET_CREATE_ORDER_JOB_QUERY,
        variables: { jobId }
      })
      .pipe(
        map((response: any) => {
          if (response?.errors?.length) {
            throw createGraphqlClientError(response.errors);
          }

          const job =
            response?.getCreateOrderJob ??
            response?.data?.getCreateOrderJob;

          return this.normalizeJob(job);
        })
      );
  }

  /**
   * createOrder + polling de getCreateOrderJob hasta COMPLETED / FAILED / timeout.
   * En local el job suele venir COMPLETED de inmediato.
   */
  createOrderWithPolling(
    input: CreateOrderInput
  ): Observable<CreateOrderJobResult> {
    const startedAt = Date.now();
    this.createOrderBusySubject.next(true);

    return this.createOrder(input).pipe(
      switchMap((job) => this.waitForCreateOrderJob(job, startedAt)),
      finalize(() => this.createOrderBusySubject.next(false))
    );
  }

  updateOrderScheduledPaymentDate(
    input: UpdateOrderScheduledPaymentDateInput
  ): Observable<UpdateOrderResult> {
    return this.mutateOrderUpdate(
      UPDATE_ORDER_SCHEDULED_PAYMENT_DATE_MUTATION,
      input,
      'updateOrderScheduledPaymentDate'
    );
  }

  updateOrderReservationNumber(
    input: UpdateOrderReservationNumberInput
  ): Observable<UpdateOrderResult> {
    return this.mutateOrderUpdate(
      UPDATE_ORDER_RESERVATION_NUMBER_MUTATION,
      input,
      'updateOrderReservationNumber'
    );
  }

  updateOrderReservationStatus(
    input: UpdateOrderReservationStatusInput
  ): Observable<UpdateOrderResult> {
    return this.mutateOrderUpdate(
      UPDATE_ORDER_RESERVATION_STATUS_MUTATION,
      input,
      'updateOrderReservationStatus'
    );
  }

  deleteOrder(input: DeleteOrderInput): Observable<UpdateOrderResult> {
    return this.mutateOrderUpdate(
      DELETE_ORDER_MUTATION,
      input,
      'deleteOrder'
    );
  }

  createOrderPdfDownloadUrl(
    orderId: string,
    disposition?: 'inline' | 'attachment' | null
  ): Observable<OrderPdfDownloadUrlResult> {
    const variables: { orderId: string; disposition?: string } = { orderId };
    if (disposition) {
      variables.disposition = disposition;
    }

    return this.graph
      .mutateGraphQLCore<{
        createOrderPdfDownloadUrl: OrderPdfDownloadUrlResult;
      }>({
        mutation: CREATE_ORDER_PDF_DOWNLOAD_URL_MUTATION,
        variables
      })
      .pipe(map((data: any) => data?.createOrderPdfDownloadUrl));
  }

  retryGenerateOrderPdf(
    orderId: string
  ): Observable<RetryGenerateOrderPdfResult> {
    return this.graph
      .mutateGraphQLCore<{
        retryGenerateOrderPdf: RetryGenerateOrderPdfResult;
      }>({
        mutation: RETRY_GENERATE_ORDER_PDF_MUTATION,
        variables: { orderId }
      })
      .pipe(map((data: any) => data?.retryGenerateOrderPdf));
  }

  /** Una página de OS disponibles (cursor lastKey opaco). */
  getOrdersAvailablePage(
    input: GetOrdersAvailableInput = {}
  ): Observable<GetOrdersAvailableResult> {
    return this.graph
      .queryGraphQLCore<{ getOrdersAvailablePage: GetOrdersAvailableResult }>({
        query: GET_ORDERS_AVAILABLE_PAGE_QUERY,
        variables: {
          input: {
            limit: input.limit ?? ORDERS_AVAILABLE_PAGE_LIMIT,
            lastKey: input.lastKey ?? null
          }
        }
      })
      .pipe(
        map((data: any) => ({
          items: data?.getOrdersAvailablePage?.items ?? [],
          lastKey: data?.getOrdersAvailablePage?.lastKey ?? null
        }))
      );
  }

  /**
   * Carga getOrdersAvailablePage página a página.
   * Llama onPage tras cada respuesta para pintar UI progresiva.
   * Reenvía exactamente el lastKey string del response; no lo parsea.
   */
  async loadOrdersAvailablePageProgressively(
    onPage: OrdersAvailableProgressCallback,
    options: {
      limit?: number;
      isCancelled?: () => boolean;
    } = {}
  ): Promise<OrderAvailableItem[]> {
    const limit = options.limit ?? ORDERS_AVAILABLE_PAGE_LIMIT;
    const items: OrderAvailableItem[] = [];
    let lastKey: string | null = null;
    let page = 0;

    do {
      if (options.isCancelled?.()) {
        return items;
      }

      const result: GetOrdersAvailableResult = await firstValueFrom(
        this.getOrdersAvailablePage({
          limit,
          lastKey
        })
      );

      if (options.isCancelled?.()) {
        return items;
      }

      const pageItems = result.items ?? [];
      items.push(...pageItems);
      lastKey = result.lastKey ?? null;
      page += 1;

      onPage({
        items: pageItems,
        loadedCount: items.length,
        hasMore: Boolean(lastKey),
        pageIndex: page - 1
      });
    } while (lastKey && page < ORDERS_AVAILABLE_MAX_PAGES);

    return items;
  }

  private waitForCreateOrderJob(
    initialJob: CreateOrderJobResult,
    startedAt: number
  ): Observable<CreateOrderJobResult> {
    const terminalOrThrow = (
      job: CreateOrderJobResult
    ): Observable<CreateOrderJobResult> => {
      const status = job.status?.trim().toUpperCase();

      if (status === CreateOrderJobStatus.COMPLETED) {
        return of(job);
      }

      if (status === CreateOrderJobStatus.FAILED) {
        logOrderDevDetail('createOrder.lastError', job.lastError);
        return throwError(() => ({
          graphQLErrors: [
            {
              message: job.lastError || 'CREATE_ORDER_JOB_FAILED',
              extensions: {
                code: job.errorCode || 'CREATE_ORDER_JOB_FAILED',
                userMessage: job.userMessage
              }
            }
          ]
        }));
      }

      if (Date.now() - startedAt > CREATE_ORDER_JOB_TIMEOUT_MS) {
        return throwError(() => ({
          graphQLErrors: [
            {
              message: 'TIMEOUT_CREATE_ORDER_JOB',
              extensions: { code: 'TIMEOUT_CREATE_ORDER_JOB' }
            }
          ]
        }));
      }

      return of(job);
    };

    if (!isCreateOrderJobInProgress(initialJob.status)) {
      return terminalOrThrow(initialJob);
    }

    return timer(0, CREATE_ORDER_JOB_POLL_INTERVAL_MS).pipe(
      switchMap(() => {
        if (Date.now() - startedAt > CREATE_ORDER_JOB_TIMEOUT_MS) {
          return throwError(() => ({
            graphQLErrors: [
              {
                message: 'TIMEOUT_CREATE_ORDER_JOB',
                extensions: { code: 'TIMEOUT_CREATE_ORDER_JOB' }
              }
            ]
          }));
        }

        return this.getCreateOrderJob(initialJob.jobId);
      }),
      takeWhile((job) => isCreateOrderJobInProgress(job.status), true),
      last(),
      switchMap((job) => terminalOrThrow(job)),
      catchError((error) => throwError(() => error))
    );
  }

  private normalizeJob(job: any): CreateOrderJobResult {
    return {
      jobId: job?.jobId ?? '',
      status: job?.status ?? CreateOrderJobStatus.FAILED,
      errorCode: job?.errorCode ?? null,
      lastError: job?.lastError ?? null,
      userMessage: job?.userMessage ?? null,
      createdOrders: job?.createdOrders ?? [],
      rejectedServices: job?.rejectedServices ?? [],
      warnings: job?.warnings ?? []
    };
  }

  private mutateOrderUpdate<TInput extends { orderId: string }>(
    mutation: string,
    input: TInput,
    field: string
  ): Observable<UpdateOrderResult> {
    return this.graph
      .mutateGraphQLCore<Record<string, UpdateOrderResult>>({
        mutation,
        variables: { input }
      })
      .pipe(map((data: any) => data?.[field] as UpdateOrderResult));
  }
}
